import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import SaveInquiryModal from './SaveInquiryModal';
import { apiService, addObjection, addFAQ } from '../services/apiService';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { Button } from './ui/Button';
import { useConfirm } from './ui/ConfirmDialog';
import { ADD_TO_KNOWLEDGE_BASE_LABEL } from '../constants/labels';

interface InquiryData {
  sessionId: string;
  sessionName: string;
  userEmail: string;
  userId: string;
  callStartTime: string;
  inquiry: {
    question: string;
    answer?: string;
    category: string;
    timestamp: string;
    relevance_score: number;
    isProcessed: boolean;
  };
}

interface NewInquiriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  inquiries: InquiryData[];
  loading?: boolean;
  onInquiryProcessed: () => void; // Callback to refresh analytics
}

const NewInquiriesModal: React.FC<NewInquiriesModalProps> = ({
  isOpen,
  onClose,
  inquiries,
  loading = false,
  onInquiryProcessed
}) => {
  const { isAdmin } = useRoleAccess();
  const confirm = useConfirm();
  const [selectedSessionId, setSelectedSessionId] = useState<string>('all');
  const [showSaveModal, setShowSaveModal] = useState<InquiryData | null>(null);
  const [savingInquiry, setSavingInquiry] = useState<string | null>(null);
  const [processedInquiries, setProcessedInquiries] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Group inquiries by session
  const sessionGroups = useMemo(() => {
    const groups = new Map<string, {
      sessionId: string;
      sessionName: string;
      userEmail: string;
      callStartTime: string;
      inquiries: InquiryData[];
    }>();

    inquiries.forEach(item => {
      if (!groups.has(item.sessionId)) {
        groups.set(item.sessionId, {
          sessionId: item.sessionId,
          sessionName: item.sessionName,
          userEmail: item.userEmail,
          callStartTime: item.callStartTime,
          inquiries: []
        });
      }
      groups.get(item.sessionId)!.inquiries.push(item);
    });

    return Array.from(groups.values()).sort((a, b) =>
      new Date(b.callStartTime).getTime() - new Date(a.callStartTime).getTime()
    );
  }, [inquiries]);

  // Filter inquiries by selected session
  const filteredInquiries = useMemo(() => {
    if (selectedSessionId === 'all') {
      return inquiries;
    }
    return inquiries.filter(item => item.sessionId === selectedSessionId);
  }, [inquiries, selectedSessionId]);

  // The "Mark as Processed" button: nothing is saved, so the admin confirms first.
  const handleMarkAsProcessed = async (inquiry: InquiryData) => {
    const confirmed = await confirm({
      title: 'Mark as processed without saving?',
      message: 'This will hide the inquiry from your unprocessed list, but it will NOT be saved to the FAQs or Objections knowledge base.',
      confirmLabel: 'Mark as processed',
      variant: 'destructive',
    });

    if (!confirmed) {
      return;
    }
    await markInquiryProcessed(inquiry);
  };

  // Also what the save modal calls once it has used an existing entry or added a
  // variation: that already saved, so asking "without saving?" would be wrong.
  const markInquiryProcessed = async (inquiry: InquiryData) => {
    try {
      await apiService.put(`/admin/sessions/${inquiry.sessionId}/inquiry-processed`, {
        question: inquiry.inquiry.question
      });

      // Update UI
      setProcessedInquiries(prev => new Set([...prev, inquiry.inquiry.question]));
      onInquiryProcessed(); // Refresh analytics count
      setSuccess('Inquiry marked as processed');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Failed to mark as processed:', err);
      setError('Failed to mark inquiry as processed');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleSaveNewInquiry = async (
    inquiry: InquiryData,
    type: 'objection' | 'faq',
    editedData: { question: string; answer: string; category: string }
  ) => {
    try {
      setSavingInquiry(inquiry.inquiry.question);
      setError(null);

      // First, save to the dictionary (objection or FAQ)
      if (type === 'objection') {
        await addObjection(editedData.question, editedData.answer, editedData.category, true, null);
      } else {
        await addFAQ(editedData.question, editedData.answer, editedData.category);
      }

      // Show success message
      setSuccess(`Successfully added to the ${type === 'objection' ? 'objections' : 'FAQs'} knowledge base`);
      setTimeout(() => setSuccess(null), 8000);

      // Then, mark as processed
      try {
        await apiService.put(`/admin/sessions/${inquiry.sessionId}/inquiry-processed`, {
          question: inquiry.inquiry.question
        });

        // Update UI
        setProcessedInquiries(prev => new Set([...prev, inquiry.inquiry.question]));
        onInquiryProcessed(); // Refresh analytics count
      } catch (err: any) {
        console.error('Failed to persist processed state:', err);
        setSuccess(prev => prev + ' (Note: Could not mark as processed in session)');
      }

      setShowSaveModal(null);

      // Scroll to top to show success message
      const modalContent = document.getElementById('new-inquiries-modal-content');
      if (modalContent) {
        modalContent.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error: any) {
      console.error('Failed to save inquiry:', error);
      setError(error.message || 'Failed to save inquiry to the knowledge base');
      setTimeout(() => setError(null), 5000);
      throw error; // Re-throw to let SaveInquiryModal handle it
    } finally {
      setSavingInquiry(null);
    }
  };

  if (!isOpen) return null;

  const unprocessedCount = filteredInquiries.filter(
    item => !item.inquiry.isProcessed && !processedInquiries.has(item.inquiry.question)
  ).length;

  return (
    <div className="modal-backdrop">
      <div className="relative max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col modal-panel">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h3 className="heading-3">New Inquiries</h3>
            <p className="text-sm text-ink-secondary mt-1">
              Total: {filteredInquiries.length} inquiries | Unprocessed: {unprocessedCount}
            </p>
          </div>
          <Button variant="tertiary" size="sm" onClick={onClose} className="!p-1">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>

        {/* Success/Error Messages */}
        <div className="px-6 pt-4">
          {success && (
            <div className="mb-4 p-4 bg-success-tint border border-success rounded-lg flex items-center">
              <CheckCircleIcon className="text-success mr-2" style={{ fontSize: '1.25rem' }} />
              <span className="text-success">{success}</span>
            </div>
          )}

          {error && (
            <div className="mb-4 p-4 bg-error-tint border border-error rounded-lg text-error">
              {error}
            </div>
          )}
        </div>

        {/* Session Filter Dropdown */}
        <div className="px-6 pb-4">
          <label htmlFor="session-filter" className="block text-sm font-medium text-ink-secondary mb-2">
            Filter by Session:
          </label>
          <select
            id="session-filter"
            value={selectedSessionId}
            onChange={(e) => setSelectedSessionId(e.target.value)}
            className="w-full px-3 py-2 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature"
          >
            <option value="all">All Sessions ({inquiries.length} inquiries)</option>
            {sessionGroups.map(session => (
              <option key={session.sessionId} value={session.sessionId}>
                {session.sessionName} - {session.userEmail} ({session.inquiries.length} inquiries) - {format(new Date(session.callStartTime), 'MMM dd, yyyy')}
              </option>
            ))}
          </select>
        </div>

        {/* Inquiries List */}
        <div id="new-inquiries-modal-content" className="flex-1 overflow-y-auto px-6 pb-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-signature"></div>
            </div>
          ) : filteredInquiries.length === 0 ? (
            <div className="text-center py-12 text-ink-muted">
              No inquiries found for the selected period
            </div>
          ) : (
            <div className="space-y-4">
              {filteredInquiries.map((item, index) => {
                const isProcessed = item.inquiry.isProcessed || processedInquiries.has(item.inquiry.question);
                const isCurrentlySaving = savingInquiry === item.inquiry.question;

                return (
                  <div
                    key={`${item.sessionId}-${index}`}
                    className={`p-4 rounded-lg border ${
                      isProcessed ? 'bg-surface-subtle border-hairline' : 'bg-warning-tint border-warning'
                    }`}
                  >
                    {/* Session Info */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-ink-muted">
                            {format(new Date(item.callStartTime), 'MMM dd, yyyy HH:mm')}
                          </span>
                          <span className="text-xs text-ink-muted">•</span>
                          <span className="text-xs text-ink-secondary">{item.sessionName}</span>
                          <span className="text-xs text-ink-muted">•</span>
                          <span className="text-xs text-ink-secondary">{item.userEmail}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-surface-sunken text-ink-secondary">
                            {item.inquiry.category}
                          </span>
                          <span className="text-xs text-ink-muted">
                            Relevance: {(item.inquiry.relevance_score * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isProcessed ? (
                          <span className="px-3 py-1 text-xs font-medium text-success bg-success-tint rounded-full">
                            Processed
                          </span>
                        ) : isAdmin ? (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleMarkAsProcessed(item)}
                              disabled={isCurrentlySaving}
                            >
                              Mark as Processed
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => setShowSaveModal(item)}
                              disabled={isCurrentlySaving}
                            >
                              {isCurrentlySaving ? 'Processing...' : ADD_TO_KNOWLEDGE_BASE_LABEL}
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {/* Question */}
                    <div className="mb-2">
                      <div className="text-sm font-medium text-ink">
                        {item.inquiry.question}
                      </div>
                    </div>

                    {/* Answer */}
                    {item.inquiry.answer && (
                      <div className="mt-2 text-sm text-ink-secondary">
                        <div className="font-medium mb-1">Answer:</div>
                        <div className="pl-3 border-l-2 border-ink-border">
                          {item.inquiry.answer}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      {/* Save New Inquiry Modal */}
      {showSaveModal && (
        <SaveInquiryModal
          inquiry={{
            question: showSaveModal.inquiry.question,
            answer: showSaveModal.inquiry.answer,
            category: showSaveModal.inquiry.category
          }}
          onClose={() => setShowSaveModal(null)}
          onSave={(inquiry, type, editedData) => handleSaveNewInquiry(showSaveModal, type, editedData)}
          onMarkAsProcessed={() => markInquiryProcessed(showSaveModal)}
          isSaving={savingInquiry === showSaveModal.inquiry.question}
        />
      )}
    </div>
  );
};

export default NewInquiriesModal;
